<!-- Renders a Mermaid diagram of business entities and their relationships. -->
<!-- Supports flowchart, LR/TB direction, and PNG download. -->
<template>
    <div class="entities-diagram-wrapper">
        <div class="diagram-toolbar">
            <SelectButton
                v-model="direction"
                :options="directionOptions"
                :allowEmpty="false"
                optionLabel="label"
                optionValue="value"
                size="small"
            >
                <template #option="{ option }">
                    <i :class="option.icon" v-tooltip.top="option.label" />
                </template>
            </SelectButton>
            <SelectButton
                v-model="showAttributes"
                :options="showAttributesOptions"
                :allowEmpty="false"
                optionLabel="label"
                optionValue="value"
                size="small"
            >
                <template #option="{ option }">
                    <i :class="option.icon" v-tooltip.top="option.label" />
                </template>
            </SelectButton>
            <Button
                v-tooltip.top="'Download as PNG'"
                icon="pi pi-download"
                size="small"
                severity="secondary"
                outlined
                @click="downloadPng"
            />
        </div>
        <div ref="containerRef" class="entities-diagram"></div>
    </div>
    <Teleport to="body">
        <div
            v-if="hoveredEntityId"
            class="entity-hover-card"
            :style="{ left: hoverCardPos.x + 'px', top: hoverCardPos.y + 'px' }"
            @mouseenter="clearHoverTimer"
            @mouseleave="startHoverTimer"
        >
            <GenericCard
                :entityId="hoveredEntityId"
                :entityType="EntityType.BUSINESS_ENTITY"
                :showPath="false"
            />
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import panzoom from "panzoom";
import type { PanZoom } from "panzoom";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import GenericCard from "@/components/EntityInfo/GenericCard.vue";
import {
    BusinessEntity,
    useBusinessEntitiesStore,
} from "@/stores/business-entities-store";
import { EntityType } from "@/stores/entity-store";
import { useUIManager } from "@/stores/ui-store";

type Direction = "LR" | "TB";

const props = defineProps<{
    entities: BusinessEntity[];
}>();

const containerRef = ref<HTMLElement | null>(null);
const { darkMode } = useUIManager();
const entitiesStore = useBusinessEntitiesStore();
const route = useRoute();
const router = useRouter();

const DIAGRAM_PREFS_KEY = "diagram-view-preferences";
const loadPrefs = (): { direction?: Direction; showAttributes?: boolean } => {
    try {
        return JSON.parse(localStorage.getItem(DIAGRAM_PREFS_KEY) ?? "{}");
    } catch {
        return {};
    }
};
const savedPrefs = loadPrefs();

const direction = ref<Direction>(savedPrefs.direction ?? "TB");
const showAttributes = ref<boolean>(savedPrefs.showAttributes ?? false);
let activePanzoom: PanZoom | null = null;

const hoveredEntityId = ref<string | null>(null);
const hoverCardPos = ref({ x: 0, y: 0 });
let hoverTimer: ReturnType<typeof setTimeout> | null = null;

const clearHoverTimer = () => {
    if (hoverTimer) {
        clearTimeout(hoverTimer);
        hoverTimer = null;
    }
};
const startHoverTimer = () => {
    hoverTimer = setTimeout(() => {
        hoveredEntityId.value = null;
    }, 100);
};

const directionOptions = [
    { label: "Vertical", value: "TB" as Direction, icon: "pi pi-arrows-v" },
    { label: "Horizontal", value: "LR" as Direction, icon: "pi pi-arrows-h" },
];

watch([direction, showAttributes], () => {
    localStorage.setItem(
        DIAGRAM_PREFS_KEY,
        JSON.stringify({
            direction: direction.value,
            showAttributes: showAttributes.value,
        })
    );
});

const showAttributesOptions = [
    {
        label: "Hide attributes",
        value: false,
        icon: "pi pi-sitemap",
    },
    {
        label: "Show attributes",
        value: true,
        icon: "pi pi-bars",
    },
];

const entityEntities = computed(() => props.entities);

const entityIds = computed(
    () => new Set(entityEntities.value.map((e) => e.identity))
);

const nodeId = (id: string) => id.replaceAll("-", "_");

/** Sanitise a string to a valid Mermaid identifier (no leading digit, no spaces). */
const safeMarkdown = (s: string) => {
    return s.replace(/`/g, "");
};

const buildMermaidSource = (): string => {
    const lines = [`flowchart ${direction.value}`];

    for (const entity of entityEntities.value) {
        let text = `**${entity.name}**\n\n`;

        if (showAttributes.value) {
            for (const attr of entitiesStore.getEntityAttributes(
                entity.identity
            )) {
                text += `${attr.name.trim()} · _${attr.type.trim()}_\n`;
            }
        }
        lines.push(
            `    ${nodeId(entity.identity)}["\`${safeMarkdown(text)}\`"]`
        );
    }

    lines.push(
        `    classDef animate stroke-dasharray: 12\\,2 ,stroke-dashoffset: 900,animation: dash 90s linear infinite;`
    );

    let idx = 0;
    for (const entity of entityEntities.value) {
        for (const rel of entity.relationships) {
            if (
                rel.direction === "outgoing" &&
                entityIds.value.has(rel.other_id)
            ) {
                lines.push(
                    `    ${nodeId(entity.identity)} e${idx}@-->|"${rel.type}"| ${nodeId(rel.other_id)}`,
                    `    class e${idx} animate`
                );
                idx++;
            }
        }
    }

    return lines.join("\n");
};

const cssVar = (name: string) =>
    window.getComputedStyle(document.body).getPropertyValue(name).trim();

const renderDiagram = async () => {
    const container = containerRef.value;
    if (!container) return;

    activePanzoom?.dispose();
    activePanzoom = null;
    hoveredEntityId.value = null;
    container.style.backgroundPosition = "";
    container.style.backgroundSize = "";

    if (showAttributes.value) {
        await entitiesStore.fetchAttributesForClassDiagram(
            entityEntities.value
        );
    }

    try {
        const mermaid = (await import("mermaid")).default;

        // For flowchart we draw our own border and want it transparent.
        mermaid.initialize({
            startOnLoad: false,
            theme: "base",
            securityLevel: "strict",
            fontFamily: "inherit",
            suppressErrorRendering: true,
            themeVariables: darkMode.value
                ? {
                      background: cssVar("--p-content-background"),
                      primaryColor: cssVar("--surface-background-mild"),
                      primaryTextColor: cssVar("--stekz-white"),
                      primaryBorderColor: "#00000000",
                      lineColor: cssVar("--stekz-primary-color"),
                      secondaryColor: cssVar("--p-content-background"),
                      edgeLabelBackground: cssVar("--p-content-background"),
                  }
                : {
                      background: cssVar("--p-content-background"),
                      primaryColor: cssVar("--surface-background-mild"),
                      primaryTextColor: cssVar("--palette-grey-500"),
                      primaryBorderColor: "#00000000",
                      lineColor: cssVar("--text-color"),
                      secondaryColor: cssVar("--p-content-background"),
                      edgeLabelBackground: cssVar("--p-content-background"),
                  },
        });

        const source = buildMermaidSource();
        const parseResult = await mermaid.parse(source, {
            suppressErrors: true,
        });

        if (!parseResult) {
            throw new Error("Invalid Mermaid diagram syntax");
        }

        const id = `mermaid-${Math.random().toString(36).substring(2, 11)}`;
        const { svg } = await mermaid.render(id, source);

        const diagram = document.createElement("div");
        diagram.innerHTML = svg;

        // Add a bottom border line to each node, matching the entity-card border-bottom.
        // Stroke color is set via inline style so it wins over SVG presentation attributes.
        const borderColor = cssVar("--stekz-primary-color");
        const borderWidth = cssVar("--stekz-border-width");
        diagram
            .querySelectorAll<SVGRectElement>(".node rect")
            .forEach((rect) => {
                const x = parseFloat(rect.getAttribute("x") ?? "0");
                const y = parseFloat(rect.getAttribute("y") ?? "0");
                const w = parseFloat(rect.getAttribute("width") ?? "0");
                const h = parseFloat(rect.getAttribute("height") ?? "0");

                const line = document.createElementNS(
                    "http://www.w3.org/2000/svg",
                    "line"
                );
                line.setAttribute("x1", String(x));
                line.setAttribute("y1", String(y + h));
                line.setAttribute("x2", String(x + w));
                line.setAttribute("y2", String(y + h));
                line.setAttribute("class", "node-bottom-border");
                line.style.stroke = borderColor;
                line.style.strokeWidth = borderWidth;
                rect.parentElement?.appendChild(line);
            });

        // Build a lookup from Mermaid node id → entity identity.
        const nodeIdToEntityId = new Map(
            props.entities.map((e: BusinessEntity) => [
                e.identity.replaceAll("-", "_"),
                e.identity,
            ])
        );

        const wireNode = (nodeEl: SVGGElement, entityId: string) => {
            nodeEl.setAttribute("id", `entity-node-${entityId}`);

            nodeEl.addEventListener("mouseenter", () => {
                clearHoverTimer();
                hoveredEntityId.value = entityId;
                const rect = nodeEl.getBoundingClientRect();
                hoverCardPos.value = {
                    x: rect.left + rect.width / 2,
                    y: rect.top - 8,
                };
            });
            nodeEl.addEventListener("mouseleave", startHoverTimer);

            // Distinguish a real click from a pan gesture by tracking drag distance.
            let dragStartX = 0;
            let dragStartY = 0;
            nodeEl.addEventListener("mousedown", (e) => {
                dragStartX = e.clientX;
                dragStartY = e.clientY;
            });
            nodeEl.addEventListener("click", (e) => {
                const dx = e.clientX - dragStartX;
                const dy = e.clientY - dragStartY;
                if (dx * dx + dy * dy > 25) return; // >5px movement = pan, not click
                router.push({
                    name: "businessEntityPage",
                    params: {
                        orgName: route.params.orgName,
                        stekName: route.params.stekName,
                        businessEntityId: entityId,
                    },
                });
            });
        };

        // Mermaid v11 prepends the render id to all node ids:
        //   flowchart:    {svgId}-flowchart-{nodeId}-{N}
        // Both diagram types use the ".node" class in v11.
        const idPattern = /flowchart-(.+)-\d+$/;

        diagram.querySelectorAll<SVGGElement>(".node").forEach((nodeEl) => {
            const svgId = nodeEl.getAttribute("id") ?? "";
            const match = svgId.match(idPattern);
            if (!match) return;
            const entityId = nodeIdToEntityId.get(match[1]);
            if (!entityId) return;
            wireNode(nodeEl, entityId);
        });

        container.innerHTML = "";
        container.appendChild(diagram);

        activePanzoom = panzoom(diagram, {
            beforeWheel: (e) => !(e.metaKey || e.ctrlKey),
            zoomDoubleClickSpeed: 1,
        });

        // Sync the container's CSS dot background with panzoom so it pans and zooms
        // with the diagram content. background-position shifts the tile phase;
        // background-size scales the tile to match the zoom level.
        const syncBackground = () => {
            const { x, y, scale } = activePanzoom!.getTransform();
            container.style.backgroundPosition = `${x}px ${y}px`;
            container.style.backgroundSize = `${20 * scale}px ${20 * scale}px`;
        };
        activePanzoom.on("transform", syncBackground);

        // Center the diagram after layout is painted
        requestAnimationFrame(() => {
            const svgEl = diagram.querySelector("svg");
            if (!svgEl) return;
            const containerRect = container.getBoundingClientRect();
            const svgRect = svgEl.getBoundingClientRect();
            activePanzoom?.moveTo(
                Math.max(0, (containerRect.width - svgRect.width) / 2),
                Math.max(0, (containerRect.height - svgRect.height) / 2)
            );
            syncBackground();
        });
    } catch (error) {
        console.error("Failed to render diagram:", error);
        container.innerHTML = `<p class="diagram-error">Failed to render diagram: ${error}</p>`;
    }
};

interface TextRun {
    text: string;
    bold: boolean;
    italic: boolean;
}

/** Recursively walks an HTML node, collecting styled text runs grouped by visual line. */
const collectTextLines = (
    node: Node,
    bold: boolean,
    italic: boolean,
    lines: TextRun[][]
): void => {
    if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? "";
        if (text) lines[lines.length - 1].push({ text, bold, italic });
        return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    const isBold = bold || tag === "strong" || tag === "b";
    const isItalic = italic || tag === "em" || tag === "i";

    if (tag === "br") {
        lines.push([]);
        return;
    }

    const isBlock = ["p", "div", "h1", "h2", "h3", "h4", "h5", "h6"].includes(
        tag
    );
    if (isBlock && lines[lines.length - 1].length > 0) lines.push([]);

    for (const child of Array.from(el.childNodes)) {
        collectTextLines(child, isBold, isItalic, lines);
    }

    if (isBlock) lines.push([]);
};

/**
 * Converts a Mermaid <foreignObject> to an SVG <text> with multi-line,
 * bold, and italic support — needed because canvas taints from foreignObject.
 */
const makeSvgText = (fo: Element, textColor: string): SVGTextElement => {
    const x = parseFloat(fo.getAttribute("x") ?? "0");
    const y = parseFloat(fo.getAttribute("y") ?? "0");
    const w = parseFloat(fo.getAttribute("width") ?? "0");
    const h = parseFloat(fo.getAttribute("height") ?? "0");

    const lines: TextRun[][] = [[]];
    collectTextLines(fo, false, false, lines);
    while (lines.length > 0 && lines[0].length === 0) lines.shift();
    while (lines.length > 0 && lines[lines.length - 1].length === 0)
        lines.pop();

    const fontSize = 14;
    const lineHeight = 21;
    const cx = x + w / 2;
    const startY = y + (h - lines.length * lineHeight) / 2 + fontSize;

    const textEl = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "text"
    ) as SVGTextElement;
    textEl.setAttribute("text-anchor", "middle");
    textEl.setAttribute("font-family", "sans-serif");
    textEl.setAttribute("font-size", String(fontSize));
    textEl.setAttribute("fill", textColor);

    if (lines.length === 0) {
        textEl.setAttribute("x", String(cx));
        textEl.setAttribute("y", String(y + h / 2));
        return textEl;
    }

    lines.forEach((runs, i) => {
        const tspan = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "tspan"
        );
        tspan.setAttribute("x", String(cx));
        if (i === 0) {
            tspan.setAttribute("y", String(startY));
        } else {
            tspan.setAttribute("dy", String(lineHeight));
        }

        if (runs.length === 0) {
            tspan.textContent = " ";
        } else {
            for (const run of runs) {
                const inner = document.createElementNS(
                    "http://www.w3.org/2000/svg",
                    "tspan"
                );
                if (run.bold) inner.setAttribute("font-weight", "bold");
                if (run.italic) inner.setAttribute("font-style", "italic");
                inner.textContent = run.text;
                tspan.appendChild(inner);
            }
        }

        textEl.appendChild(tspan);
    });

    return textEl;
};

const downloadPng = () => {
    const container = containerRef.value;
    if (!container) return;

    const svgEl = container.querySelector("svg");
    if (!svgEl) return;

    // Use the viewBox dimensions for the canvas — this is the natural resolution
    // of the SVG content, independent of whatever panzoom zoom/translate is active.
    const vb = svgEl.viewBox.baseVal;
    const width = Math.ceil(vb.width);
    const height = Math.ceil(vb.height);

    const clonedSvg = svgEl.cloneNode(true) as SVGSVGElement;
    clonedSvg.setAttribute("width", String(width));
    clonedSvg.setAttribute("height", String(height));
    clonedSvg.style.overflow = "hidden";

    // Strip external font references and disable dash animations for a static PNG.
    clonedSvg.querySelectorAll<SVGStyleElement>("style").forEach((el) => {
        el.textContent = (el.textContent ?? "")
            .replace(/@font-face\s*\{[^}]*\}/gs, "")
            .replace(/font-family:[^;}"]+/g, "font-family: sans-serif")
            .replace(/stroke-dasharray\s*:[^;}"]+/g, "stroke-dasharray: none")
            .replace(/stroke-dashoffset\s*:[^;}"]+/g, "stroke-dashoffset: 0")
            .replace(/animation\s*:[^;}"]+/g, "animation: none");
    });

    // Remove stroke-dasharray from element attributes and inline styles —
    // Mermaid classDef inlines these as style="" properties, not HTML attributes,
    // so the CSS-rule override above is not sufficient on its own.
    clonedSvg.querySelectorAll<SVGElement>("*").forEach((el) => {
        el.style.removeProperty("stroke-dasharray");
        el.style.removeProperty("stroke-dashoffset");
        el.style.removeProperty("animation");
        el.removeAttribute("stroke-dasharray");
        el.removeAttribute("stroke-dashoffset");
    });

    // Replace each <foreignObject> with an SVG <text> — Chrome taints canvases from
    // SVG images that contain <foreignObject>. The replacement preserves multi-line
    // layout and bold/italic formatting from the Mermaid-rendered HTML inside.
    const textColor = cssVar(
        darkMode.value ? "--stekz-white" : "--palette-grey-500"
    );
    const edgeLabelBg = cssVar("--p-content-background");
    Array.from(clonedSvg.querySelectorAll("foreignObject")).forEach((fo) => {
        // Edge labels get their background from CSS on the HTML inside the foreignObject.
        // Re-create it as an SVG rect so text remains readable in the PNG.
        if (fo.closest(".edgeLabel")) {
            const x = parseFloat(fo.getAttribute("x") ?? "0");
            const y = parseFloat(fo.getAttribute("y") ?? "0");
            const w = parseFloat(fo.getAttribute("width") ?? "0");
            const h = parseFloat(fo.getAttribute("height") ?? "0");
            const bgRect = document.createElementNS(
                "http://www.w3.org/2000/svg",
                "rect"
            );
            bgRect.setAttribute("x", String(x));
            bgRect.setAttribute("y", String(y));
            bgRect.setAttribute("width", String(w));
            bgRect.setAttribute("height", String(h));
            bgRect.setAttribute("fill", edgeLabelBg);
            fo.parentNode?.insertBefore(bgRect, fo);
        }

        fo.parentNode?.replaceChild(makeSvgText(fo, textColor), fo);
    });

    // Inline the bottom border stroke so it renders correctly outside the DOM.
    const borderColor = cssVar("--stekz-primary-color");
    clonedSvg
        .querySelectorAll<SVGLineElement>(".node-bottom-border")
        .forEach((line) => line.setAttribute("stroke", borderColor));

    // Prepend a background rect using the current theme background color.
    const bgRect = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "rect"
    );
    bgRect.setAttribute("width", String(width));
    bgRect.setAttribute("height", String(height));
    bgRect.setAttribute("fill", cssVar("--p-content-background"));
    clonedSvg.insertBefore(bgRect, clonedSvg.firstChild);

    const svgData = new XMLSerializer().serializeToString(clonedSvg);
    const svgUrl = URL.createObjectURL(
        new Blob([svgData], { type: "image/svg+xml;charset=utf-8" })
    );

    const img = new Image();
    img.onload = () => {
        const scale = Math.min(window.devicePixelRatio || 1, 2);
        const canvas = document.createElement("canvas");
        canvas.width = width * scale;
        canvas.height = height * scale;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
            URL.revokeObjectURL(svgUrl);
            return;
        }
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(svgUrl);

        canvas.toBlob((blob) => {
            if (!blob) return;
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "domain-model.png";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, "image/png");
    };
    img.onerror = () => URL.revokeObjectURL(svgUrl);
    img.src = svgUrl;
};

onMounted(renderDiagram);

watch(
    [() => props.entities, darkMode, direction, showAttributes],
    renderDiagram,
    {
        deep: true,
    }
);
</script>

<style scoped lang="scss">
.entities-diagram-wrapper {
    position: relative;

    .diagram-toolbar {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        z-index: 10;
        display: flex;
        gap: 0.5rem;
        align-items: center;
        padding: 0.25rem 0.5rem;
        background: color-mix(
            in srgb,
            var(--p-content-background) 90%,
            transparent
        );
        border-radius: var(--p-border-radius-sm, 4px);
    }
}

.entities-diagram {
    overflow: hidden;
    width: 100%;
    min-height: 300px;
    cursor: grab;
    &:active {
        cursor: grabbing;
    }
    background-color: var(--p-content-background);
    background-image: radial-gradient(
        circle closest-side,
        color-mix(in srgb, var(--stekz-outline) 60%, transparent) 10%,
        transparent 10%
    );
    background-size: 20px 20px;

    --shadow-color: color-mix(in srgb, var(--text-color) 24%, transparent);

    :deep(svg) {
        width: 100%;
        height: auto;
        // Allow drop-shadow filters to render outside the SVG's viewBox bounds.
        overflow: visible;
    }

    // Match entity-card shadow and hover scale.
    // transform-box: fill-box ensures scale(1.01) expands from the node's own center,
    // not the SVG viewport origin.
    :deep(.node) {
        cursor: pointer;
        filter: drop-shadow(0 1px 3px var(--shadow-color));
        transform-box: fill-box;
        transform-origin: center;
        transition:
            filter 0.25s ease,
            transform 0.25s ease;
    }

    // Bottom border matching entity-card's border-bottom (color set via inline style in renderDiagram)
    :deep(.node-bottom-border) {
        pointer-events: none;
    }

    // Darken node background on hover, matching entity-card hover state.
    // !important needed to override Mermaid's inline fill attributes.
    :deep(.node rect),
    :deep(.node circle),
    :deep(.node ellipse),
    :deep(.node polygon) {
        transition: fill 0.25s ease;
    }

    :deep(.node:hover rect),
    :deep(.node:hover circle),
    :deep(.node:hover ellipse),
    :deep(.node:hover polygon) {
        fill: var(--surface-background-mild) !important;
    }
}

.diagram-error {
    color: var(--p-red-500);
    padding: 1rem;
}
</style>

<style lang="scss">
// Teleported outside the component's scoped styles — must be global.
.entity-hover-card {
    position: fixed;
    z-index: 9999;
    transform: translateX(-50%) translateY(-100%);
    pointer-events: all;
    margin-top: -8px;
}
</style>
